import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Paperclip, ArrowRight, CheckCircle2 } from "lucide-react";
import { VIEWPORT_60, EASE } from "@/lib/animations";
import { APPLY_EMAIL, MAILTO_SIMPLE } from "./jobData";

const initialForm = {
  nome: "",
  email: "",
  whatsapp: "",
  cidadeUf: "",
  graduacao: "",
  coren: "",
  cnpj: "",
  presencial: "",
  experiencia: "",
  mensagem: "",
  cvName: "",
};

const inputCls =
  "w-full px-4 py-3 rounded-xl bg-white border border-[#E5EAF2] text-[#1F2937] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#3787F5] focus:ring-4 focus:ring-[#3787F5]/12 transition-all duration-200";
const labelCls = "text-sm font-semibold text-[#1F2937]";
const chipCls =
  "cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E5EAF2] text-sm text-[#1F2937] hover:border-[#3787F5] transition-colors";
const chipActiveCls =
  "inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#3532B3] to-[#3787F5] text-white text-sm font-semibold shadow-[0_6px_16px_rgba(53,50,179,0.28)]";

const TEXT_FIELDS = [
  { key: "nome", label: "Nome completo *", type: "text", placeholder: "Seu nome", required: true, testid: "form-nome" },
  { key: "email", label: "E-mail *", type: "email", placeholder: "voce@email.com", required: true, testid: "form-email" },
  { key: "whatsapp", label: "WhatsApp", type: "tel", placeholder: "(00) 00000-0000", testid: "form-whatsapp" },
  { key: "cidadeUf", label: "Cidade/UF", type: "text", placeholder: "Ex.: Vila Velha/ES", testid: "form-cidade" },
];

const RADIO_QUESTIONS = [
  { key: "graduacao", label: "Possui graduação completa em Enfermagem?", options: ["Sim", "Não"], testid: "form-graduacao" },
  { key: "coren", label: "Possui COREN ativo?", options: ["Sim", "Não"], testid: "form-coren" },
  { key: "cnpj", label: "Possui CNPJ ativo?", options: ["Sim", "Não", "Tenho disponibilidade para abertura"], testid: "form-cnpj" },
  { key: "presencial", label: "Tem disponibilidade para capacitação presencial em Vila Velha/ES?", options: ["Sim", "Não"], testid: "form-presencial" },
];

const TEXTAREAS = [
  { key: "experiencia", label: "Experiência com DS, auditoria, operadora ou entrevista qualificada", placeholder: "Conte um pouco da sua experiência", testid: "form-experiencia" },
  { key: "mensagem", label: "Mensagem", placeholder: "Escreva algo que gostaria de compartilhar", testid: "form-mensagem" },
];

const buildMailto = (form) => {
  const lines = [
    "Olá, Previsa Saúde.",
    "",
    "Gostaria de me candidatar à vaga de Enfermeiro(a) Administrativo – Entrevista Médica Qualificada.",
    "",
    `Nome: ${form.nome}`,
    `E-mail: ${form.email}`,
    `WhatsApp: ${form.whatsapp}`,
    `Cidade/UF: ${form.cidadeUf}`,
    `Graduação em Enfermagem: ${form.graduacao || "-"}`,
    `COREN ativo: ${form.coren || "-"}`,
    `CNPJ: ${form.cnpj || "-"}`,
    `Disponibilidade para capacitação presencial em Vila Velha/ES: ${form.presencial || "-"}`,
    "",
    "Experiência com DS, auditoria, operadora ou entrevista qualificada:",
    form.experiencia || "-",
    "",
    "Mensagem:",
    form.mensagem || "-",
    "",
    "Anexarei meu currículo em resposta a este e-mail.",
    "",
    "Atenciosamente,",
  ];
  const subject = encodeURIComponent("Vaga Enfermeiro Adm – EMQ | Candidatura");
  const body = encodeURIComponent(lines.join("\n"));
  return `mailto:${APPLY_EMAIL}?subject=${subject}&body=${body}`;
};

const RadioGroup = ({ name, options, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-2 mt-2" data-testid={testid}>
    {options.map((opt) => (
      <label key={opt} className={value === opt ? chipActiveCls : chipCls}>
        <input
          type="radio"
          name={name}
          value={opt}
          checked={value === opt}
          onChange={(e) => onChange(e.target.value)}
          className="sr-only"
        />
        {opt}
      </label>
    ))}
  </div>
);

const SubmittedView = ({ form, onReset }) => (
  <div
    data-testid="form-submitted"
    className="flex flex-col items-center text-center py-6"
  >
    <div className="w-16 h-16 rounded-full bg-[#12DDB5]/15 text-[#0b9a7c] flex items-center justify-center mb-5">
      <CheckCircle2 size={30} strokeWidth={2.2} />
    </div>
    <h3 className="text-2xl font-bold text-[#1F2937]">
      Seu e-mail foi preparado!
    </h3>
    <p className="mt-3 text-[#64748B] max-w-md">
      Abrimos seu cliente de e-mail com os dados preenchidos. Basta{" "}
      <strong>anexar seu currículo</strong> e enviar para{" "}
      <a href={`mailto:${APPLY_EMAIL}`} className="text-[#3532B3] font-semibold">
        {APPLY_EMAIL}
      </a>
      .
    </p>
    <a
      href={buildMailto(form)}
      className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#3532B3] to-[#3787F5] text-white font-semibold"
    >
      <Mail size={18} />
      Reabrir e-mail
    </a>
    <button
      type="button"
      onClick={onReset}
      className="mt-3 text-sm text-[#64748B] hover:text-[#3532B3]"
    >
      Enviar outra candidatura
    </button>
  </div>
);

const FileUploadField = ({ cvName, onFile }) => (
  <div>
    <label className={labelCls}>Anexar currículo (opcional)</label>
    <label
      className="mt-2 flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-dashed border-[#E5EAF2] hover:border-[#3787F5] cursor-pointer transition-colors"
      data-testid="form-file"
    >
      <Paperclip size={18} className="text-[#3787F5]" />
      <span className="text-sm text-[#64748B]">
        {cvName || "Selecionar arquivo PDF ou DOC"}
      </span>
      <input
        type="file"
        onChange={onFile}
        className="sr-only"
        accept=".pdf,.doc,.docx"
      />
    </label>
    <p className="mt-2 text-xs text-[#94A3B8]">
      Se este ambiente não permitir upload, você poderá anexar o currículo
      diretamente no e-mail que abriremos.
    </p>
  </div>
);

const FormFields = ({ form, set, onFile }) => (
  <>
    <div className="grid md:grid-cols-2 gap-5">
      {TEXT_FIELDS.map((f) => (
        <div key={f.key}>
          <label className={labelCls}>{f.label}</label>
          <input
            required={f.required}
            data-testid={f.testid}
            type={f.type}
            value={form[f.key]}
            onChange={set(f.key)}
            className={`${inputCls} mt-2`}
            placeholder={f.placeholder}
          />
        </div>
      ))}
    </div>

    {RADIO_QUESTIONS.map((q) => (
      <div key={q.key}>
        <label className={labelCls}>{q.label}</label>
        <RadioGroup
          name={q.key}
          testid={q.testid}
          options={q.options}
          value={form[q.key]}
          onChange={set(q.key)}
        />
      </div>
    ))}

    {TEXTAREAS.map((t) => (
      <div key={t.key}>
        <label className={labelCls}>{t.label}</label>
        <textarea
          data-testid={t.testid}
          rows={3}
          value={form[t.key]}
          onChange={set(t.key)}
          className={`${inputCls} mt-2 resize-none`}
          placeholder={t.placeholder}
        />
      </div>
    ))}

    <FileUploadField cvName={form.cvName} onFile={onFile} />
  </>
);

const formReveal = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT_60,
  transition: { duration: 0.6, ease: EASE },
};

export const ApplicationForm = () => {
  const [form, setForm] = useState(initialForm);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (v) =>
    setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.nome || !form.email) return;
    window.location.href = buildMailto(form);
    setSubmitted(true);
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    setForm((prev) => ({ ...prev, cvName: f ? f.name : "" }));
  };

  const onReset = () => {
    setSubmitted(false);
    setForm(initialForm);
  };

  return (
    <motion.div
      {...formReveal}
      className="bg-white rounded-3xl border border-[#E5EAF2] shadow-[0_18px_50px_rgba(15,23,42,0.06)] p-6 md:p-10"
    >
      {submitted ? (
        <SubmittedView form={form} onReset={onReset} />
      ) : (
        <form onSubmit={onSubmit} className="space-y-6" data-testid="apply-form">
          <FormFields form={form} set={set} onFile={onFile} />

          <p className="text-xs text-[#64748B] leading-relaxed">
            <strong>Aviso LGPD:</strong> Ao enviar seus dados, você autoriza a
            Previsa Saúde a utilizar as informações exclusivamente para fins de
            recrutamento e seleção.
          </p>

          <button
            type="submit"
            data-testid="form-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-[#3532B3] to-[#3787F5] text-white font-bold shadow-[0_12px_28px_rgba(53,50,179,0.28)] hover:shadow-[0_14px_36px_rgba(53,50,179,0.38)] hover:-translate-y-0.5 transition-all duration-300"
          >
            Enviar candidatura
            <ArrowRight size={18} strokeWidth={2.2} />
          </button>

          <p className="text-center text-sm text-[#64748B]">
            Ou envie direto pelo e-mail:{" "}
            <a
              href={MAILTO_SIMPLE}
              className="text-[#3532B3] font-semibold hover:underline"
            >
              {APPLY_EMAIL}
            </a>
          </p>
        </form>
      )}
    </motion.div>
  );
};
